import { useState } from 'react';
import type { ChangeEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import IconButton from '@mui/material/IconButton';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import { Typography, SegmentedButton } from '@proposify/ui';
import { fontSize } from '@proposify/tokens/semantic-tokens/fontSize';

import { PanelContainerStyled } from './DiscountPanel';
import { AmountFieldStyled, AmountRowStyled, PanelSectionStyled, SectionHeaderStyled, TypeToggleGroupStyled } from './DiscountSection';
import { getDiscountValidationErrorKey } from './helpers';
import type { DiscountType } from './types';

export interface FooterTotalRowPanelProps {
  /** Row label shown in the header — the user-entered footer row title. */
  title: string;
  type: DiscountType;
  /** Footer rows store UI-scale values ('10' = 10%) — no engine-fraction mapping here. */
  amount: string;
  /** Row subtotal as a decimal string; drives the exceeds-subtotal validation. */
  subtotal: string;
  currencySymbol: string;
  /** TAX rows skip the exceeds-subtotal rule — a fixed tax may exceed the subtotal. */
  skipMaxFixedValidation?: boolean;
  /** Fires with the validated amount+type pair — debounced persistence path. */
  onAmountCommit: (amount: string, type: DiscountType) => void;
  /** Fires with the validated amount+type pair — immediate persistence path. */
  onTypeCommit: (amount: string, type: DiscountType) => void;
  onDelete: () => void;
}

export const FooterTotalRowPanel = ({
  title,
  type,
  amount,
  subtotal,
  currencySymbol,
  skipMaxFixedValidation = false,
  onAmountCommit,
  onTypeCommit,
  onDelete,
}: FooterTotalRowPanelProps) => {
  const { t } = useTranslation();
  const [localType, setLocalType] = useState<DiscountType>(type);
  const [localAmount, setLocalAmount] = useState<string>(amount);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const amountIdentifier = localType === 'percentage' ? '%' : currencySymbol;

  const validate = (raw: string, nextType: DiscountType): string | null => {
    const validationErrorKey = getDiscountValidationErrorKey(raw, nextType, subtotal);
    if (skipMaxFixedValidation && validationErrorKey === 'pricing.discount.error_max_fixed') return null;
    return validationErrorKey;
  };

  const handleAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    setLocalAmount(raw);
    const validationErrorKey = validate(raw, localType);
    setErrorKey(validationErrorKey);
    if (!validationErrorKey && raw !== '') {
      onAmountCommit(raw, localType);
    }
  };

  const handleTypeChange = (_event: MouseEvent<HTMLElement>, newType: DiscountType) => {
    if (newType === localType) return;
    setLocalType(newType);
    const validationErrorKey = validate(localAmount, newType);
    setErrorKey(validationErrorKey);
    if (!validationErrorKey && localAmount !== '') {
      onTypeCommit(localAmount, newType);
    }
  };

  return (
    <PanelContainerStyled data-testid="footer-total-row-panel">
      <PanelSectionStyled>
        <SectionHeaderStyled>
          <Typography fontWeight={600} fontSize={fontSize['3']}>
            {title}
          </Typography>
          <IconButton
            data-testid="footer-total-row-panel-delete"
            size="small"
            aria-label={t('pricing.footer_row_panel.delete_row')}
            onClick={onDelete}
          >
            <DeleteOutlineRounded fontSize="small" />
          </IconButton>
        </SectionHeaderStyled>

        <AmountRowStyled>
          <AmountFieldStyled
            data-testid="footer-total-row-panel-amount"
            label={t('pricing.discount.amount')}
            value={localAmount}
            placeholder="-"
            error={errorKey !== null}
            helperText={errorKey ? t(errorKey) : null}
            size="small"
            onChange={handleAmountChange}
            endAdornment={localType === 'percentage' ? amountIdentifier : undefined}
            startAdornment={localType === 'fixed' ? amountIdentifier : undefined}
          />

          <TypeToggleGroupStyled aria-label={t('pricing.discount.type_label')} value={localType} onChange={handleTypeChange} size="small">
            <SegmentedButton value="percentage" data-testid="footer-total-row-panel-type-percentage">
              {t('pricing.discount.type_percentage')}
            </SegmentedButton>
            <SegmentedButton value="fixed" data-testid="footer-total-row-panel-type-fixed">
              {currencySymbol}
            </SegmentedButton>
          </TypeToggleGroupStyled>
        </AmountRowStyled>
      </PanelSectionStyled>
    </PanelContainerStyled>
  );
};
